import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, MapPin, Home, Calendar, Building,
  Save, Loader2, Camera, Edit2, Shield, CheckCircle, 
  Clock, DollarSign, Briefcase, Globe, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  
  // Personal info state
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // Homeowner profile state
  const [homeownerProfile, setHomeownerProfile] = useState({
    address: '',
    city: '',
    province_state: '',
    postal_code: '',
    property_type: '',
    ownership_status: '',
    timeline: '',
    property_value_estimate: '',
    year_built: '',
    square_footage: ''
  });
  
  // Sponsor profile state
  const [sponsorProfile, setSponsorProfile] = useState({
    company_name: '',
    business_type: '',
    license_number: '',
    service_areas: [],
    bio: '',
    website: '',
    profile_image: ''
  });

  const isSponsor = user?.role === 'sponsor';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchProfiles();
  }, [token]);

  const fetchProfiles = async () => {
    try {
      // Set personal info from user
      setPersonalInfo({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || ''
      });

      // Fetch homeowner profile
      const homeownerRes = await fetch(`${API}/api/profile/homeowner`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (homeownerRes.ok) {
        const data = await homeownerRes.json();
        if (data) {
          setHomeownerProfile({
            address: data.address || '',
            city: data.city || '',
            province_state: data.province_state || '',
            postal_code: data.postal_code || '',
            property_type: data.property_type || '',
            ownership_status: data.ownership_status || '',
            timeline: data.timeline || '',
            property_value_estimate: data.property_value_estimate || '',
            year_built: data.year_built || '',
            square_footage: data.square_footage || ''
          });
        }
      }

      // Fetch sponsor profile if applicable
      if (isSponsor || isAdmin) {
        const sponsorRes = await fetch(`${API}/api/profile/sponsor`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (sponsorRes.ok) {
          const data = await sponsorRes.json();
          if (data) {
            setSponsorProfile({
              company_name: data.company_name || '',
              business_type: data.business_type || '',
              license_number: data.license_number || '',
              service_areas: data.service_areas || [],
              bio: data.bio || '',
              website: data.website || '',
              profile_image: data.profile_image || ''
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveHomeownerProfile = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/profile/homeowner`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...homeownerProfile,
          property_value_estimate: homeownerProfile.property_value_estimate ? parseFloat(homeownerProfile.property_value_estimate) : null,
          year_built: homeownerProfile.year_built ? parseInt(homeownerProfile.year_built) : null,
          square_footage: homeownerProfile.square_footage ? parseInt(homeownerProfile.square_footage) : null
        })
      });

      if (!response.ok) throw new Error('Failed to save');
      toast.success('Property profile saved!');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const saveSponsorProfile = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/profile/sponsor`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sponsorProfile)
      });

      if (!response.ok) throw new Error('Failed to save');
      toast.success('Sponsor profile saved!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <User className="w-6 h-6 text-[#FF3B30]" />
            Profile Settings
          </h1>
          <p className="text-neutral-500 mt-1">Manage your account and preferences</p>
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-[#FF3B30] to-[#DC2626] px-6 py-8">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-white text-3xl font-bold">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <button className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-neutral-100">
                <Camera className="w-4 h-4 text-neutral-600" />
              </button>
            </div>
            <div className="text-white">
              <h2 className="text-xl font-bold">{user?.name}</h2>
              <p className="text-white/80">{user?.email}</p>
              <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm capitalize">
                {user?.role}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="p-6">
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
            <TabsTrigger value="property" data-testid="tab-property">Property</TabsTrigger>
            {(isSponsor || isAdmin) && (
              <TabsTrigger value="business" data-testid="tab-business">Business</TabsTrigger>
            )}
          </TabsList>

          {/* Personal Info Tab */}
          <TabsContent value="personal" className="mt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Full Name
                </Label>
                <Input
                  id="name"
                  value={personalInfo.name}
                  onChange={(e) => setPersonalInfo({...personalInfo, name: e.target.value})}
                  className="mt-2"
                  data-testid="input-name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={personalInfo.email}
                  disabled
                  className="mt-2 bg-neutral-50"
                  data-testid="input-email"
                />
                <p className="text-xs text-neutral-500 mt-1">Email cannot be changed</p>
              </div>
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  value={personalInfo.phone}
                  onChange={(e) => setPersonalInfo({...personalInfo, phone: e.target.value})}
                  placeholder="+1 (416) 555-0000"
                  className="mt-2"
                  data-testid="input-phone"
                />
              </div>
            </div>
            
            <div className="flex justify-end pt-4 border-t border-neutral-100">
              <Button 
                onClick={() => toast.info('Personal info updates coming soon')}
                className="bg-[#FF3B30] hover:bg-[#DC2626]"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </TabsContent>

          {/* Property Tab */}
          <TabsContent value="property" className="mt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Street Address
                </Label>
                <Input
                  value={homeownerProfile.address}
                  onChange={(e) => setHomeownerProfile({...homeownerProfile, address: e.target.value})}
                  placeholder="123 Main Street"
                  className="mt-2"
                  data-testid="input-address"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={homeownerProfile.city}
                  onChange={(e) => setHomeownerProfile({...homeownerProfile, city: e.target.value})}
                  placeholder="Toronto"
                  className="mt-2"
                  data-testid="input-city"
                />
              </div>
              <div>
                <Label>Province/State</Label>
                <Select 
                  value={homeownerProfile.province_state} 
                  onValueChange={(v) => setHomeownerProfile({...homeownerProfile, province_state: v})}
                >
                  <SelectTrigger className="mt-2" data-testid="select-province">
                    <SelectValue placeholder="Select province" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ontario">Ontario</SelectItem>
                    <SelectItem value="British Columbia">British Columbia</SelectItem>
                    <SelectItem value="Alberta">Alberta</SelectItem>
                    <SelectItem value="Quebec">Quebec</SelectItem>
                    <SelectItem value="Manitoba">Manitoba</SelectItem>
                    <SelectItem value="Saskatchewan">Saskatchewan</SelectItem>
                    <SelectItem value="Nova Scotia">Nova Scotia</SelectItem>
                    <SelectItem value="New Brunswick">New Brunswick</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Postal Code</Label>
                <Input
                  value={homeownerProfile.postal_code}
                  onChange={(e) => setHomeownerProfile({...homeownerProfile, postal_code: e.target.value})}
                  placeholder="M5V 1A1"
                  className="mt-2"
                  data-testid="input-postal"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Property Type
                </Label>
                <Select 
                  value={homeownerProfile.property_type} 
                  onValueChange={(v) => setHomeownerProfile({...homeownerProfile, property_type: v})}
                >
                  <SelectTrigger className="mt-2" data-testid="select-property-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="house">Single Family House</SelectItem>
                    <SelectItem value="condo">Condo</SelectItem>
                    <SelectItem value="townhouse">Townhouse</SelectItem>
                    <SelectItem value="duplex">Duplex</SelectItem>
                    <SelectItem value="semi-detached">Semi-Detached</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ownership Status</Label>
                <Select 
                  value={homeownerProfile.ownership_status} 
                  onValueChange={(v) => setHomeownerProfile({...homeownerProfile, ownership_status: v})}
                >
                  <SelectTrigger className="mt-2" data-testid="select-ownership">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="renting">Renting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Your Plans
                </Label>
                <Select 
                  value={homeownerProfile.timeline} 
                  onValueChange={(v) => setHomeownerProfile({...homeownerProfile, timeline: v})}
                >
                  <SelectTrigger className="mt-2" data-testid="select-timeline">
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="selling_soon">Selling soon (within 6 months)</SelectItem>
                    <SelectItem value="renovating_soon">Renovating soon</SelectItem>
                    <SelectItem value="browsing">Just browsing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Estimated Property Value
                </Label>
                <Input
                  type="number"
                  value={homeownerProfile.property_value_estimate}
                  onChange={(e) => setHomeownerProfile({...homeownerProfile, property_value_estimate: e.target.value})}
                  placeholder="500000"
                  className="mt-2"
                  data-testid="input-property-value"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Year Built
                </Label>
                <Input
                  type="number"
                  value={homeownerProfile.year_built}
                  onChange={(e) => setHomeownerProfile({...homeownerProfile, year_built: e.target.value})}
                  placeholder="1990"
                  className="mt-2"
                  data-testid="input-year-built"
                />
              </div>
              <div>
                <Label>Square Footage</Label>
                <Input
                  type="number"
                  value={homeownerProfile.square_footage}
                  onChange={(e) => setHomeownerProfile({...homeownerProfile, square_footage: e.target.value})}
                  placeholder="2000"
                  className="mt-2"
                  data-testid="input-sqft"
                />
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-neutral-100">
              <Button 
                onClick={saveHomeownerProfile}
                disabled={saving}
                className="bg-[#FF3B30] hover:bg-[#DC2626]"
                data-testid="save-property-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Property Info
              </Button>
            </div>
          </TabsContent>

          {/* Business Tab (Sponsors) */}
          {(isSponsor || isAdmin) && (
            <TabsContent value="business" className="mt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Company Name
                  </Label>
                  <Input
                    value={sponsorProfile.company_name}
                    onChange={(e) => setSponsorProfile({...sponsorProfile, company_name: e.target.value})}
                    placeholder="Your Company Name"
                    className="mt-2"
                    data-testid="input-company-name"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Business Type
                  </Label>
                  <Select 
                    value={sponsorProfile.business_type} 
                    onValueChange={(v) => setSponsorProfile({...sponsorProfile, business_type: v})}
                  >
                    <SelectTrigger className="mt-2" data-testid="select-business-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="realtor">Real Estate Agent</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="brand">Brand/Retailer</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    License Number
                  </Label>
                  <Input
                    value={sponsorProfile.license_number}
                    onChange={(e) => setSponsorProfile({...sponsorProfile, license_number: e.target.value})}
                    placeholder="Optional"
                    className="mt-2"
                    data-testid="input-license"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Website
                  </Label>
                  <Input
                    value={sponsorProfile.website}
                    onChange={(e) => setSponsorProfile({...sponsorProfile, website: e.target.value})}
                    placeholder="https://yourwebsite.com"
                    className="mt-2"
                    data-testid="input-website"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Bio / Description
                  </Label>
                  <Textarea
                    value={sponsorProfile.bio}
                    onChange={(e) => setSponsorProfile({...sponsorProfile, bio: e.target.value})}
                    placeholder="Tell homeowners about yourself and your services..."
                    rows={4}
                    className="mt-2"
                    data-testid="input-bio"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-neutral-100">
                <Button 
                  onClick={saveSponsorProfile}
                  disabled={saving}
                  className="bg-[#FF3B30] hover:bg-[#DC2626]"
                  data-testid="save-business-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Business Info
                </Button>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Upgrade to Sponsor Card */}
      {!isSponsor && !isAdmin && (
        <div className="bg-gradient-to-r from-neutral-900 to-neutral-800 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#FF3B30] rounded-xl flex items-center justify-center">
              <Briefcase className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">Become a Sponsor</h3>
              <p className="text-neutral-400 text-sm">
                Get access to high-intent homeowner leads in your area
              </p>
            </div>
            <Button className="bg-white text-neutral-900 hover:bg-neutral-100">
              Learn More
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
